import {AlertTicket, EscalatedTicketCreateBody, EscalatedTicketUpdateBody, Ticket, TicketReferenceData, TicketStatusId} from '@shared/services/api/tickets/tickets.interface';
import {Task} from '@shared/services/api/task.interface';
import moment from 'moment-timezone';
import {EventEmitter} from '@angular/core';
import {AttachmentPreviewUserAction} from '@app/shared/components/attachments/attachments/attachments.component';

export enum TicketOperation {
  CREATE,
  UPDATE,
  DELETE,
  DISMISS
}

export interface TicketOperationResult<T extends Ticket> {
  operation: TicketOperation;
  ticket: Partial<T>;
  affectedTickets?: Partial<Ticket>[];
  newAlertTasks?: Task[];
}

export interface TicketUnassignedOption {
  id: string;
  fullName: string;
}

export interface RetrievedTicketData<T extends Ticket> {
  // The data in this Map will be sorted. Will be taking advantage that Maps in JavaScript preserve insertion order
  tickets: Map<string, T>;
  // The offset for the next fetch of tickets. This is used instead of just relying on tickets.length to ensure that if the state is out of
  // sync with the server (e.g. new tickets have been created on the server) that we do not make multiple requests with the same offset,
  // i.e. the offset is increased for every request
  offset: number;
  // True when all tickets for a given site have been fetched
  allFetched: boolean;
  // Used to load enough tickets such that the ticket pane does not fit into the browser viewport, i.e. the user has to scroll to see the
  // bottom of the ticket list, so that infinite scroll can work
  initialLoadCompleted: boolean;
}


export const UNASSIGNED_ID = 'UNASSIGNED';

export const NO_COMMENTS_ID = 'NO_COMMENTS';

export const RULE_IDS_SEPARATOR = ',';


export interface TicketComponentPart {
  afterTicketUpdate: (ticketOperationResult?: TicketOperationResult<Ticket>) => void;
}

export const TICKET_SEARCH_TS_EXCLUSIVE_ADJUSTMENT = moment.duration(1, 'day');

export interface AddCommentConfig {
  showAddComment: boolean;
  commentText?: string;
}

export interface TicketComponentWithAttachmentPreview {
  readonly attachmentPreviewUserAction: EventEmitter<AttachmentPreviewUserAction>
}

export interface TicketUpsertData extends TicketReferenceData {
  linkedAlerts: AlertTicket[] | null;
  ticketBody: EscalatedTicketUpdateBody | EscalatedTicketCreateBody;
  comment?: string;
}

export interface TicketUpsertForm {
  showErrors(): void;
}


export enum EscalatedTicketExecutableAction {
  RESOLVED = TicketStatusId.STATUS_RESOLVED,
  ON_HOLD = TicketStatusId.STATUS_ON_HOLD,
  NOT_DOING = TicketStatusId.STATUS_NOT_DOING,
  APPROVE,
  EDIT,
  EMAIL,
  LINK_TICKET,
}


export const ESCALATED_TICKETS_EXECUTABLE_ACTIONS_BY_STATUS_ID = new Map([
  [null, new Set([
    // Allow tickets with any status to be edited, so that they can be deleted even if there's no status
    EscalatedTicketExecutableAction.EDIT,
  ])],
  [TicketStatusId.STATUS_NEW, new Set([
    EscalatedTicketExecutableAction.APPROVE,
    EscalatedTicketExecutableAction.ON_HOLD,
    EscalatedTicketExecutableAction.NOT_DOING,
    EscalatedTicketExecutableAction.RESOLVED,
    EscalatedTicketExecutableAction.EDIT,
    EscalatedTicketExecutableAction.EMAIL
  ])],
  [TicketStatusId.STATUS_IN_PROGRESS, new Set([
    EscalatedTicketExecutableAction.ON_HOLD,
    EscalatedTicketExecutableAction.NOT_DOING,
    EscalatedTicketExecutableAction.RESOLVED,
    EscalatedTicketExecutableAction.EDIT,
    EscalatedTicketExecutableAction.EMAIL
  ])],
  [TicketStatusId.STATUS_ON_HOLD, new Set([
    EscalatedTicketExecutableAction.NOT_DOING,
    EscalatedTicketExecutableAction.RESOLVED,
    EscalatedTicketExecutableAction.EDIT,
    EscalatedTicketExecutableAction.EMAIL
  ])],
  [TicketStatusId.STATUS_NOT_DOING, new Set([
    EscalatedTicketExecutableAction.ON_HOLD,
    EscalatedTicketExecutableAction.RESOLVED,
    EscalatedTicketExecutableAction.EDIT,
    EscalatedTicketExecutableAction.EMAIL
  ])],
  [TicketStatusId.STATUS_RESOLVED, new Set([
    EscalatedTicketExecutableAction.EDIT,
    EscalatedTicketExecutableAction.EMAIL
  ])]
]);
